import React, { useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Grid,
  MenuItem,
  Select,
  TextField,
} from '@nextiva/next-ui-components';

import { ContentContainer } from 'components';
import { Page } from 'containers';
import tooltip from 'icons/tooltip.svg';
import { getDomains } from 'api/domainApi';
import { getEmails } from 'api/emailApi';

import { getEmailsList } from './helpers';

// Type of domain to exclude
const FREE = 'FREE';
const domainOptions = (domains) => {
  return Object.keys(domains)
    .filter((name) => !domains[name].includes(FREE))
    .map((name, i) => ({ label: name, value: name, key: i }));
};

const defaultDomain = {
  value: 'nextiva.com',
  key: 'nextiva.com',
  label: 'nextiva.com',
  isDefault: true,
};

const goToVerifyTab = () => {
  window.location.hash = `/email`;
  localStorage.setItem('emailTabIndex', 0);
};

const goToEmailTab = () => {
  window.location.hash = `/email`;
};

const EmailSetup = () => {
  const [error, setError] = useState(false);
  const [domains, setDomains] = useState({});
  const [emails, setEmails] = useState([]);
  const [value, setValue] = useState();
  const [pristine, setPristine] = useState(true);
  const [email, setEmail] = useState({
    name: '',
    domain: 'nextiva.com',
  });

  const [filteredDomains, setFilteredDomains] = useState([]);

  useEffect(() => {
    getDomains().then((_domains) => setDomains(_domains));
    getEmails().then((_emails) => {
      setEmails(getEmailsList(_emails));
    });
  }, []);

  useEffect(() => {
    setFilteredDomains(domainOptions(domains));
  }, [domains]);

  const handleChange = ({ target }) => {
    setError(false);
    setEmail({ ...email, [target.id]: target.value });
    setValue(target.value);
    setPristine(!target.value);
  };

  const handeDomainChange = (event) => {
    setError(false);
    setEmail({ ...email, domain: event.target.value });
  };

  const handleKeyEvents = (event) => {
    setEmail({ ...email, [event.id]: event.target.value });
    setValue(event.target.value);
    setPristine(false);
  };

  const handleNextClick = () => {
    if (emails.find((item) => item === `${email.name}@${email.domain}`))
      setError(true);
  };

  const BreadCrumbComp = () => {
    return (
      <div className="_m-email-setup-breadcrumb-header">
        <div
          tabIndex={-1}
          role="button"
          onClick={goToEmailTab}
          onKeyDown={handleKeyEvents}
          onKeyUp={handleKeyEvents}
          onKeyPress={handleKeyEvents}
          data-testid="PlatformAdminUI-EmailSetup-BreadCrumbComp"
        >
          Team Emails / <span> Email Setup</span>
        </div>
      </div>
    );
  };

  return (
    <ContentContainer>
      <Page title=" / Email Setup" titleComponent={<BreadCrumbComp />}>
        <h2 className="_m-email-setup-heading Heading2">
          Set up a team email address in two easy steps.
        </h2>
        <Grid
          container
          className="pa-email-setup _m-email-setup-wrapper"
          direction="row"
          justifyContent="space-around"
          alignItems="center"
        >
          <Grid item className="Caption _m-email-setup-title-active">
            Email Address
          </Grid>
          <Grid item className="Caption _m-email-setup-title-secondary">
            Team
          </Grid>
        </Grid>
        <div className="_m-email-setup-email-bar">
          <div className="_m-email-setup-teams-bar"></div>
        </div>
        <h3 className="_m-email-setup-subHeading Heading3">
          Set up a team email address
        </h3>
        <span className="_m-email-setup-content">
          Enable this team to send and receive emails from a dedicated address.
        </span>
        <div className="_m-email-setup-form-wrapper">
          <div className="_m-email-setup-hint Hint">EMAIL ADDRESS</div>
          <div className="row">
            <div className="col _m-left-container">
              <TextField
                className="_m-email-setup-input"
                id="name"
                value={value}
                onChange={handleChange}
                helperText={!!error && 'Email address should be unique'}
                error={error}
                data-testid="PlatformAdminUI-EmailSetup-TextField"
              />
            </div>
            <div className="col _m-center-left-container">
              <span className="_m-email-setup-divider">@</span>
            </div>
            <div className="col _m-center-right-container">
              <Select
                id="email-setup-select"
                className="_m-domain-dropdown"
                value={email.domain}
                onChange={handeDomainChange}
                data-testid="PlatformAdminUI-EmailSetup-SelectEmail"
              >
                {[defaultDomain, ...filteredDomains].map((opt) => (
                  <MenuItem key={opt.key} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className="col _m-right-container">
              <span className="_m-email-setup-caption">
                Don&#39;t see the domain you need?
                <br />
              </span>
              <span
                tabIndex={-1}
                className="_m-email-setup-caption _m-email-setup-link"
                role="button"
                onKeyDown={handleKeyEvents}
                onKeyUp={handleKeyEvents}
                onKeyPress={handleKeyEvents}
                onClick={goToVerifyTab}
                data-testid="PlatformAdminUI-EmailSetup-VerifyButton"
              >
                Verify a new domain
              </span>
              <img alt="tooltip" className="_m-email-setup-svg" src={tooltip} />
            </div>
          </div>
        </div>
        <Divider />
        <div className="_m-email-setup-button-wrapper">
          <Button
            className="_m-email-setup-button-cancel"
            color="secondary"
            variant="contained"
            data-testid="PlatformAdminUI-EmailSetup-CancelButton"
            onClick={goToEmailTab}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            className="_m-email-setup-button-next"
            disabled={pristine}
            onClick={handleNextClick}
            data-testid="PlatformAdminUI-EmailSetup-NextButton"
          >
            Next
          </Button>
        </div>
      </Page>
    </ContentContainer>
  );
};

export default EmailSetup;
